import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, PremiumModal, Field, Button,
  StatusBadge, LoadingState, EmptyState, DataTable,
} from "components/ui";

const BranchDetail = () => {
  const { id } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchUsers, setBranchUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [adminForm, setAdminForm] = useState({ firstName: "", lastName: "", email: "", password: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchBranch = async () => {
    try {
      const res = await axiosPrivate.get(`/api/branches/${id}`);
      setBranch(res.data);
    } catch (_) {
      toast.error("Branch not found");
      navigate("/tenant/branches");
    } finally {
      setLoading(false);
    }
  };
  const fetchUsers = async () => {
    try {
      const res = await axiosPrivate.get(`/api/branches/branch/${id}/users?role=BRANCH_ADMIN`);
      setBranchUsers(res.data);
    } catch (_) {
      toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };
  useEffect(() => { fetchBranch(); fetchUsers(); }, [id]);

  const handleDeleteBranch = async () => {
    if (!window.confirm("Delete this branch permanently?")) return;
    try {
      await axiosPrivate.delete(`/api/branches/${id}`);
      toast.success("Branch deleted");
      navigate("/tenant/branches");
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  const handleCreateAdmin = async () => {
    setSubmitting(true);
    try {
      const userRes = await axiosPrivate.post("/api/users/branch-admin", adminForm);
      const newUser = userRes.data;
      await axiosPrivate.patch(`/api/users/${newUser.id}/assign-branch`, id);
      toast.success("Branch admin created and assigned");
      setModal(false);
      setAdminForm({ firstName: "", lastName: "", email: "", password: "", phone: "" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async (userId) => {
    try {
      await axiosPrivate.patch(`/api/users/${userId}/unassign-branch`);
      toast.success("Admin unassigned");
      fetchUsers();
    } catch (_) {
      toast.error("Unassign failed");
    }
  };

  if (loading || !branch) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState /></PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 16 }}>
        <PremiumCard
          title={branch.name}
          subtitle={`Code ${branch.code}`}
          actions={
            <>
              <Button variant="secondary" size="sm" onClick={() => navigate(`/tenant/branches/edit/${id}`)}>Edit branch</Button>
              <Button variant="danger" size="sm" onClick={handleDeleteBranch}>Delete</Button>
            </>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
            <Def label="City"    value={branch.city} />
            <Def label="Address" value={branch.address} />
            <Def label="Phone"   value={branch.phone} />
            <Def label="Email"   value={branch.email} />
            <div>
              <div className="t-body-xs">Status</div>
              <div style={{ marginTop: 4 }}><StatusBadge value={branch.status} dot /></div>
            </div>
            <Def label="Default currency" value={branch.defaultCurrency} />
          </div>
        </PremiumCard>

        <PremiumCard
          title="Branch admins"
          actions={<Button variant="primary" size="sm" onClick={() => setModal(true)}>Add</Button>}
          flush
        >
          {usersLoading ? (
            <LoadingState />
          ) : branchUsers.length === 0 ? (
            <EmptyState title="No admins assigned" />
          ) : (
            <table className="dt-table" style={{ width: "100%" }}>
              <tbody>
                {branchUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.firstName} {u.lastName}</div>
                      <div className="t-body-sm t-muted">{u.email}</div>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Button variant="ghost" size="sm" onClick={() => handleUnassign(u.id)}>Unassign</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </PremiumCard>
      </div>

      <PremiumModal
        isOpen={modal}
        toggle={() => setModal(false)}
        title="Add branch admin"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" loading={submitting} onClick={handleCreateAdmin}>Create & assign</Button>
          </>
        }
      >
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Field label="First name" required value={adminForm.firstName} onChange={(e) => setAdminForm({ ...adminForm, firstName: e.target.value })} placeholder="Ahmed" />
          <Field label="Last name"  required value={adminForm.lastName}  onChange={(e) => setAdminForm({ ...adminForm, lastName: e.target.value })}  placeholder="Ben Salah" />
          <Field label="Email"   required type="email"    value={adminForm.email}    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}    placeholder="admin@branch.com" />
          <Field label="Phone"   value={adminForm.phone}    onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}    placeholder="+216 …" />
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Password" required type="password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} placeholder="Enter a secure password" autoComplete="new-password" />
          </div>
        </div>
      </PremiumModal>
    </PageContainer>
  );
};

const Def = ({ label, value }) => (
  <div>
    <div className="t-body-xs">{label}</div>
    <div style={{ marginTop: 4, fontSize: 14, fontWeight: 500 }}>{value || "—"}</div>
  </div>
);

export default BranchDetail;
